import React, { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { MdDelete } from "react-icons/md";
import { getAuthorizationHeaders, useAuthenticationService } from "@/services/authentication/authentication-service";
import { useVariable } from "@/services/backstage/BackstageProvider";
import {
  createErrorToast,
  createSuccessToast,
} from "@/services/notification/ToastService";
import { DeleteModal } from "./DeleteModal";
import strings from "./tenantManagement.translations";
import { guardOnce } from "@/helpers/Utility";

ModuleRegistry.registerModules([AllCommunityModule]);

// =============================
// Grid layout constants
// =============================
const ROW_HEIGHT = 42;
const HEADER_HEIGHT = 42;
const MAX_VISIBLE_ROWS = 6;
const GRID_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT * MAX_VISIBLE_ROWS;

// =============================
// CORS Origin Grid
// =============================
export const CorsOriginGrid: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [user] = useAuthenticationService((s) => [s.user]);
  const authUrl = useVariable("authUrl");

  const [rowData, setRowData] = useState<string[]>([]);
  const [newOrigin, setNewOrigin] = useState("");

  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean;
    origin?: string;
  }>({ visible: false });

  const gridRef = useRef<AgGridReact>(null);

  // Guards so a second click can't fire the same add/delete request again while one is in flight.
  const inFlightAddRef = useRef<Set<true>>(new Set());
  const inFlightDeletesRef = useRef<Set<string>>(new Set());

  // =============================
  // Load client URLs and extract AllowedCorsOrigins
  // =============================
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(
          `${authUrl}/api/client-configurations/${clientId}/urls`,
          {
            headers: getAuthorizationHeaders({ includeContentType: false }),
          }
        );

        if (!res.ok) throw new Error(res.statusText);

        const data = await res.json();
        setRowData(data.allowedCorsOrigins ?? []);
      } catch (e) {
        console.error(e);
        createErrorToast(strings.corsOrigins.loadError);
      }
    };

    if (user && clientId) load();
  }, [user, clientId, authUrl]);

  // =============================
  // Add origin (base URL)
  // =============================
  const addOrigin = () => {
    if (!newOrigin.trim()) return;

    return guardOnce(inFlightAddRef.current, true, async () => {
      try {
        const res = await fetch(
          `${authUrl}/api/client-configurations/${clientId}/add-url`,
          {
            method: "POST",
            headers: getAuthorizationHeaders(),
            body: JSON.stringify(newOrigin.trim()),
          }
        );

        if (!res.ok) throw new Error("Add failed");

        const data = await res.json();
        setRowData(data.allowedCorsOrigins ?? []);
        setNewOrigin("");
        createSuccessToast(strings.corsOrigins.addSuccess);
      } catch (e) {
        console.error(e);
        createErrorToast(strings.corsOrigins.addError);
      }
    });
  };

  // =============================
  // Delete origin (base URL)
  // =============================
  const deleteOrigin = (origin: string) =>
    guardOnce(inFlightDeletesRef.current, origin, async () => {
      try {
        const res = await fetch(
          `${authUrl}/api/client-configurations/${clientId}/delete-url`,
          {
            method: "DELETE",
            headers: getAuthorizationHeaders(),
            body: JSON.stringify(origin),
          }
        );

        if (!res.ok) throw new Error("Delete failed");

        const data = await res.json();
        setRowData(data.allowedCorsOrigins ?? []);
        createSuccessToast(strings.corsOrigins.deleteSuccess);
      } catch (e) {
        console.error(e);
        createErrorToast(strings.corsOrigins.deleteError);
      }
    });

  // =============================
  // Columns
  // =============================
  const columns: ColDef[] = [
    {
      headerName: strings.corsOrigins.originColumn,
      flex: 1,
      valueGetter: (params) => params.data,
    },
    {
      headerName: "",
      width: 90,
      cellRenderer: (params: any) => (
        <button
          onClick={() =>
            setDeleteModal({ visible: true, origin: params.data })
          }
          className="p-1 rounded text-red-600 hover:bg-red-100"
        >
          <MdDelete size={22} />
        </button>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {/* Title */}
      <h2 className="text-lg font-semibold mb-3">
        {strings.corsOrigins.title}
      </h2>

      {/* Add form */}
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newOrigin}
          onChange={(e) => setNewOrigin(e.target.value)}
          placeholder={strings.corsOrigins.placeholder}
          className="flex-1 border rounded px-2 py-1"
        />
        <button
          onClick={addOrigin}
          className="px-3 py-1 bg-primary-500 text-white rounded hover:bg-primary-600"
        >
          {strings.corsOrigins.addButton}
        </button>
      </div>

      {/* Grid */}
      <div
        className="ag-theme-alpine"
        style={{ height: GRID_HEIGHT, width: "100%" }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columns}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          domLayout="normal"
          animateRows={false}
          defaultColDef={{ resizable: false }}
        />
      </div>

      {/* Delete modal */}
      {deleteModal.visible && deleteModal.origin && (
        <DeleteModal
          visible={deleteModal.visible}
          title={strings.corsOrigins.deleteModalTitle}
          message={deleteModal.origin}
          confirmLabel={strings.corsOrigins.deleteConfirm}
          cancelLabel={strings.corsOrigins.deleteCancel}
          onConfirm={() => {
            deleteOrigin(deleteModal.origin!);
            setDeleteModal({ visible: false });
          }}
          onCancel={() => setDeleteModal({ visible: false })}
        />
      )}
    </div>
  );
};
